import React, { useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';

import { Subcontent, AntdButton as Button } from '@yuhuinc/ui-library/';

import { AdditionalChargeForm } from './AdditionalChargeForm';

import styled from 'styled-components';

import { formatMoney, titleize } from 'helpers/Formatter';

import { getTranslatedString } from 'helpers/i18n';
import { media, sizes } from 'helpers/MediaHelper';
import { extractSuffix, ADDITIONAL_CHARGES } from 'helpers/DocumentTemplateHelper';

import {
  storeAvailableAdditionalCharges,
  storeCurrentAdditionalChargesForLease,
} from '../store/actions';

const StyledSubcontentContainer = styled.div`
  margin-bottom: 16px;
`;

const StyledButton = styled(Button)`
  margin-top: 10px;
  ${media.mobile`
    margin-bottom: 14px;
  `};
`;

const AdditionalChargeList = ({
  leaseDraftAdditionalCharges,
  filterForValidAdditionalCharges,
  storeAvailableAdditionalCharges,
  selectedBaseDocumentTemplate,
  conditionalAddendumsForLease,
  fixedAddendumsForLease,
  availableAdditionalCharges,
  currentAdditionalChargesForLease,
  storeCurrentAdditionalChargesForLease,
}) => {
  const [toggleNewItem, setToggleNewItem] = useState(false);
  const [currentEditableItem, setCurrentEditableItem] = useState({});
  const [isEdit, setIsEdit] = useState(false);
  const [editIndex, setEditIndex] = useState();
  const [isDelete, setDelete] = useState(false);

  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;

  useEffect(() => {
    storeAvailableAdditionalCharges(
      selectedBaseDocumentTemplate,
      fixedAddendumsForLease,
      conditionalAddendumsForLease
    );
  }, [
    storeAvailableAdditionalCharges,
    selectedBaseDocumentTemplate,
    conditionalAddendumsForLease,
    fixedAddendumsForLease,
  ]);

  useEffect(() => {
    if (leaseDraftAdditionalCharges && !isDelete) {
      storeCurrentAdditionalChargesForLease(
        leaseDraftAdditionalCharges,
        availableAdditionalCharges
      );
    }
  }, [
    availableAdditionalCharges,
    isDelete,
    leaseDraftAdditionalCharges,
    storeCurrentAdditionalChargesForLease,
  ]);

  const setValidAdditionalChargesInStore = (additionalCharges) => {
    storeCurrentAdditionalChargesForLease(additionalCharges, availableAdditionalCharges);
  };

  const getAdditionalCharges = () => {
    if (isEmpty(availableAdditionalCharges)) {
      return [];
    }
    const itemList = [];

    availableAdditionalCharges.forEach((additionalCharge) => {
      let itemAlreadyAdded = false;

      currentAdditionalChargesForLease.forEach((validCharge) => {
        if (validCharge.id === additionalCharge.id) {
          itemAlreadyAdded = true;
        }
      });

      if (!itemAlreadyAdded) {
        itemList.push({
          label: additionalCharge.name,
          value: additionalCharge.id,
        });
      }
    });

    return itemList;
  };

  const getAdditionalChargeAmount = (id) => {
    return availableAdditionalCharges.find((charge) => charge.id === id).default_amount;
  };

  const getAdditionalChargeName = (id) => {
    const itemCharge = availableAdditionalCharges.find((charge) => charge.id === id);
    if (itemCharge) {
      return itemCharge.name;
    }

    return undefined;
  };

  const handleEdit = (item, index) => {
    setIsEdit(true);
    setEditIndex(index);
    setCurrentEditableItem(item);
    setToggleNewItem(true);
  };

  const resetAfterEdit = () => {
    setIsEdit(false);
    setCurrentEditableItem({});
    setEditIndex(undefined);
  };

  const handleDelete = (index) => {
    const newValidAdditionalCharges = [...currentAdditionalChargesForLease];
    newValidAdditionalCharges.splice(index, 1);
    storeCurrentAdditionalChargesForLease(newValidAdditionalCharges);
    setDelete(true);
  };

  const additionalChargesToRender = filterForValidAdditionalCharges(
    currentAdditionalChargesForLease,
    availableAdditionalCharges
  );

  return (
    <React.Fragment>
      {!isEmpty(additionalChargesToRender) &&
        additionalChargesToRender.map((item, index) => {
          if (isEdit && index === editIndex) return null;

          return (
            <StyledSubcontentContainer>
              <Subcontent
                label={titleize(extractSuffix(item.name, ADDITIONAL_CHARGES))}
                onEditClick={() => handleEdit(item, index)}
                onDeleteClick={() => handleDelete(index)}
                hideEdit={toggleNewItem || isEdit} // hide edit icon if currently adding or editting
                sublabel={formatMoney(item.default_amount)}
                isMobile={isMobile}
                collapsed
              />
            </StyledSubcontentContainer>
          );
        })}

      {toggleNewItem ? (
        <StyledSubcontentContainer>
          <Subcontent
            label={getTranslatedString(
              'landlordPortal.leaseCreatorV2.leasePricingPage.additionalChargeLabel'
            )}
            hideEdit
            hideDelete
          >
            <AdditionalChargeForm
              setToggleNewItem={setToggleNewItem}
              getAdditionalChargeAmount={getAdditionalChargeAmount}
              getAdditionalCharges={getAdditionalCharges}
              getAdditionalChargeName={getAdditionalChargeName}
              currentEditableItem={currentEditableItem}
              setCurrentEditableItem={setCurrentEditableItem}
              validAdditionalCharges={currentAdditionalChargesForLease}
              setValidAdditionalChargesInStore={setValidAdditionalChargesInStore}
              resetAfterEdit={resetAfterEdit}
              isEdit={isEdit}
              editIndex={editIndex}
              isMobile={isMobile}
            />
          </Subcontent>
        </StyledSubcontentContainer>
      ) : (
        <StyledButton
          label={getTranslatedString('landlordPortal.leaseCreatorV2.leasePricingPage.addCharge')}
          onClick={() => {
            setToggleNewItem(true);
          }}
          size={isMobile ? 'large' : 'default'}
        />
      )}
    </React.Fragment>
  );
};

const mapStoreToProps = (store) => ({
  selectedBaseDocumentTemplate: store.landlord.leaseCreatorV2.selectedBaseDocumentTemplate,
  conditionalAddendumsForLease: store.landlord.leaseCreatorV2.conditionalAddendumsForLease,
  fixedAddendumsForLease: store.landlord.leaseCreatorV2.fixedAddendumsForLease,
  availableAdditionalCharges: store.landlord.leaseCreatorV2.availableAdditionalCharges,
  currentAdditionalChargesForLease: store.landlord.leaseCreatorV2.currentAdditionalChargesForLease,
});

const mapDispatchToProps = {
  storeAvailableAdditionalCharges,
  storeCurrentAdditionalChargesForLease,
};

export default connect(mapStoreToProps, mapDispatchToProps)(AdditionalChargeList);
